/**
 * Кнопка (§2.3 «Кнопка <Button>»).
 *
 * Варианты и размеры заданы спекой дословно. Два решения, которые видно в коде:
 *
 * 1. `disabled` не ставит атрибут `disabled`: кнопка остаётся в tab-порядке и
 *    получает `aria-disabled`, чтобы причина-текст рядом (П6) читалась
 *    скринридером. Клик и Enter/Space при этом гасятся вручную.
 * 2. `loading` не меняет ширину: спиннер 14 px выведен из потока, подпись
 *    остаётся на месте (§2.3, §11.1).
 */
import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Spinner } from './Spinner'
import './Button.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger-ghost'
export type ButtonSize = 's' | 'm' | 'l' | 'xl'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** s 24 · m 32 (по умолчанию) · l 40 · xl 44 — xl только главная кнопка подтверждения. */
  size?: ButtonSize
  loading?: boolean
  iconBefore?: ReactNode
  iconAfter?: ReactNode
  /** Растянуть на всю ширину контейнера (нижний лист на телефоне). */
  block?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'm',
    loading = false,
    iconBefore,
    iconAfter,
    block = false,
    disabled = false,
    className,
    children,
    onClick,
    onKeyDown,
    type = 'button',
    ...rest
  },
  ref,
) {
  const inert = disabled || loading

  const classes = ['btn', `btn--${variant}`, `btn--${size}`, 't-btn']
  if (size === 'l' || size === 'xl') classes.push('t-btn--l')
  if (loading) classes.push('btn--loading')
  if (block) classes.push('btn--block')
  if (className) classes.push(className)

  return (
    <button
      ref={ref}
      type={type}
      className={classes.join(' ')}
      aria-disabled={inert || undefined}
      aria-busy={loading || undefined}
      onClick={(event) => {
        if (inert) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      onKeyDown={(event) => {
        if (inert && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          return
        }
        onKeyDown?.(event)
      }}
      {...rest}
    >
      {loading ? (
        <span className="btn__spinner">
          <Spinner size={14} />
        </span>
      ) : null}
      {iconBefore}
      {children}
      {iconAfter}
    </button>
  )
})
